import { z } from 'zod';

/**
 * Typed UI block system for Bizy chat (HABITS-1294).
 *
 * Backend tools emit structured payloads that stream over the Bizy
 * channel as `event: "ui_block"`; the frontend's
 * {@link useBizyChannel} hands them off to {@link useBizyGlobalChat},
 * which attaches them to the in-flight assistant message. The
 * {@link UiBlockRenderer} then looks the block up in the registry
 * (see `src/components/bizy-side-panel/ui-blocks/registry.ts`) and
 * renders the matching component, which reads `payload.actions` to
 * decide what buttons to show.
 *
 * Adding a new block type is three files:
 *   1. A new payload + entry in `BizyUiBlockSchema` below.
 *   2. A new renderer component in `src/components/bizy-side-panel/ui-blocks/`.
 *   3. A registry entry in `.../registry.ts`.
 *
 * On the backend, a tool's `ui_blocks_from_result` returns a payload
 * matching one of these shapes — no markdown formatting rules, no
 * prompt contract, no reverse-engineering. See the README at
 * `src/components/bizy-side-panel/ui-blocks/README.md` for the full
 * walkthrough.
 *
 * Forward-compatibility: any block whose `type` isn't recognized
 * parses as {@link UnknownBizyUiBlockSchema}, which renders a
 * graceful placeholder so an older frontend + newer backend doesn't
 * crash the chat.
 */

// ─── Actions ─────────────────────────────────────────────────────────────────

/**
 * Full enough recipient profile for the give-box to build a proper
 * `@username` pill (display name + avatar) without a follow-up fetch.
 * Backend tools that know the resolved user (e.g.
 * `generate_recognition_suggestion`) populate this; clients that only
 * have a username string can fall back to the legacy `recipient`
 * field. Lives at module top because both `send_now` and
 * `open_in_givebox` actions reference it.
 */
const RecipientRefSchema = z.object({
	id: z.string(),
	username: z.string(),
	displayName: z.string(),
	profilePicUrl: z.string().nullable().optional(),
});
export type BizyUiBlockRecipientRef = z.infer<typeof RecipientRefSchema>;

const InsertIntoDraftActionSchema = z.object({
	type: z.literal('insert_into_draft'),
	/** Text to drop into the active surface's composer (with AI-touched highlight). */
	text: z.string(),
	/**
	 * Bonus id of the parent post the comment composer is attached to.
	 * The dispatcher routes the insert to the matching mounted composer
	 * via {@link useBizyActiveComposersStore}, so multiple open
	 * composers in the same feed don't collide. Required for the
	 * comment-draft flow; absent for any future general "insert into
	 * the active text input" use.
	 */
	parentBonusId: z.string().optional(),
	/**
	 * Optional add-on points amount. When present and >0, the
	 * dispatcher prefixes the inserted text with `+N ` so the
	 * composer's amount parser picks it up — without this the user
	 * sees a draft message but the points pill stays empty (silent
	 * "comment with no amount" regression).
	 */
	amount: z.number().optional(),
	/** Optional comment-surface hashtags (rare; only when the user opted in). */
	hashtags: z.array(z.string()).optional(),
	/** Optional button label override. Defaults to a translated string. */
	label: z.string().optional(),
});

const SendNowActionSchema = z.object({
	type: z.literal('send_now'),
	/** Text to post via the active surface's submit path. */
	text: z.string(),
	/** Stable payload for Bizy's commit_draft turn so the agent never has to reconstruct the approved draft from prose. */
	draft: z
		.object({
			recipient: z.union([z.string(), z.array(z.string())]).optional(),
			message: z.string(),
			amount: z.number().optional(),
			hashtags: z.array(z.string()).optional(),
		})
		.optional(),
	/**
	 * Comment surface only: the bonus id of the parent post. The
	 * dispatcher uses this to route the submit to the matching
	 * mounted comment composer via
	 * {@link useBizyActiveComposersStore}.
	 */
	parentBonusId: z.string().optional(),
	/**
	 * Recognition surface: recipient username(s) for display. Sent
	 * alongside `recipientRef` (which carries the full profile the
	 * give-box needs) so the card body can render the bare `@username`
	 * even when no full profile is available.
	 */
	recipient: z.union([z.string(), z.array(z.string())]).optional(),
	/** Full recipient profile so the give-box autoSend path can build the @ pill. */
	recipientRef: RecipientRefSchema.optional(),
	amount: z.number().optional(),
	hashtags: z.array(z.string()).optional(),
	label: z.string().optional(),
});

const OpenInGiveBoxActionSchema = z.object({
	type: z.literal('open_in_givebox'),
	/** Text to pre-fill the give box editor with (AI-touched highlighted). */
	text: z.string(),
	/** Optional recipient username(s) for display purposes (card body, fallback). */
	recipient: z.union([z.string(), z.array(z.string())]).optional(),
	/**
	 * Full recipient profile so the give-box can construct a proper
	 * `@user` pill (id, displayName, avatar) at mount time. Without
	 * this, the modal opens with just `@` and no name — the user can't
	 * actually send.
	 */
	recipientRef: RecipientRefSchema.optional(),
	/** Optional point amount to pre-fill. */
	amount: z.number().optional(),
	/** Optional hashtags (without `#` prefix) to pre-fill. */
	hashtags: z.array(z.string()).optional(),
	label: z.string().optional(),
});

const ViewPostActionSchema = z.object({
	type: z.literal('view_post'),
	/** Route to navigate to (e.g. `/bonuses/abc`). */
	url: z.string(),
	label: z.string().optional(),
});

/**
 * Hands a post off to Bizy to DRAFT an appreciation reply rather than posting
 * anything directly. Used by the appreciation card's "Add on" / "Comment"
 * buttons. The dispatcher posts the friendly `message` as a normal turn AND
 * forwards `parentBonusId` as the contextual COMMENT drafting surface, so Bizy
 * drafts an editable reply ON the existing post (a comment_draft_card carrying
 * the parent) — an add-on (`amount` > 0) or a zero-point comment — never a new
 * give-box recognition, and the visible chat never shows a raw bonus ID. The
 * post's addressing facts are resolved server-side from `parentBonusId`.
 *
 * Wire fields are `.nullable().optional()` for the usual Mongoid-null reason.
 */
const RequestDraftActionSchema = z.object({
	type: z.literal('request_draft'),
	/** Friendly, ID-free prompt posted as the visible chat turn (e.g. "Draft an add-on for the recognition Paul received."). */
	message: z.string(),
	/** 'add_on' (points-bearing) or 'comment' (zero-point). */
	intent: z.enum(['add_on', 'comment']).nullable().optional(),
	/** The post being appreciated; forwarded as `bizy_contextual_parentBonusId`. */
	parentBonusId: z.string().nullable().optional(),
	/** Suggested add-on points (add_on only). */
	amount: z.number().nullable().optional(),
	label: z.string().optional(),
});

const DismissActionSchema = z.object({
	type: z.literal('dismiss'),
	label: z.string().optional(),
});

/**
 * Confirm action on a user-admin draft/confirm card (INS-1011). The
 * admin clicking it IS the approval — the dispatcher posts `text` as a
 * fresh chat turn with `bizy_action: 'commit_user_action'` and
 * `bizy_user_draft_payload: commitPayload`, and the UserAdmin agent's
 * commit-mode prompt replays the underlying tool with `confirmed=true`.
 *
 * `commitPayload` is a JSON STRING (not a nested object) of
 * `{ action, params }`. The backend serializes it as a string on
 * purpose so its snake_case tool-input keys (e.g. `first_name`) survive
 * the node's wire camelization — we forward it verbatim, never parse or
 * re-serialize it here.
 */
const CommitUserActionSchema = z.object({
	type: z.literal('commit_user_action'),
	/** User-visible chat turn posted on confirm (e.g. "Yes, send the invite."). */
	text: z.string(),
	/** Verbatim `{ action, params }` JSON string replayed as `bizy_user_draft_payload`. */
	commitPayload: z.string(),
	label: z.string().optional(),
});

/**
 * Cancel action on a user-admin draft/confirm card (INS-1011). Discards
 * the pending action and tells the agent it was cancelled by posting
 * `text` with `bizy_action: 'cancel_user_action'` so Bizy acknowledges
 * in chat (no tool runs).
 */
const CancelUserActionSchema = z.object({
	type: z.literal('cancel_user_action'),
	/** User-visible chat turn posted on cancel (e.g. "Cancel that for now."). */
	text: z.string(),
	label: z.string().optional(),
});

export const BizyUiBlockActionSchema = z.discriminatedUnion('type', [
	InsertIntoDraftActionSchema,
	SendNowActionSchema,
	OpenInGiveBoxActionSchema,
	ViewPostActionSchema,
	DismissActionSchema,
	CommitUserActionSchema,
	CancelUserActionSchema,
	RequestDraftActionSchema,
]);

export type BizyUiBlockAction = z.infer<typeof BizyUiBlockActionSchema>;

/**
 * Accepts arrays of unknown action shapes and filters them down to the
 * valid ones. The backend is the source of truth for what actions a
 * block has, but if a NEW action type shows up that this client doesn't
 * know about, we want to silently drop it rather than fail the whole
 * block (and lose the good actions alongside the unknown one).
 */
/**
 * Tolerant list parser: drops malformed entries instead of failing the whole
 * card, and treats `null` / `undefined` / missing as an empty list. Wire
 * payloads arrive with `null` for absent values (un-`.compact`'d Mongoid
 * hashes); a bare `.default([])` accepts `undefined` but REJECTS `null`, which
 * silently fails the discriminated union and drops the entire card (same class
 * of bug as the optionalString note below).
 */
// Returns a bare ZodEffects; apply `.default([])` (always-array output) or
// `.optional()` (genuinely optional field) at the use site.
function tolerantList<Schema extends z.ZodTypeAny>(itemSchema: Schema) {
	return z.preprocess(
		(value) => (value == null ? [] : value),
		z.array(z.unknown()).transform((raw) =>
			raw.flatMap((item) => {
				const result = itemSchema.safeParse(item);
				return result.success ? [result.data] : [];
			})
		)
	);
}

const ActionListSchema = tolerantList(BizyUiBlockActionSchema).default([]);

// ─── Block payloads ──────────────────────────────────────────────────────────

const BlockIdSchema = z.string().optional();

// Optional fields are .nullable().optional() — the wire payload comes
// through with `null` for absent values from Mongoid hashes that haven't
// been .compact'd. A bare `.optional()` accepts `undefined` but REJECTS
// `null`, which silently fails the discriminated union and drops the
// entire card from the rendered document. HABITS-1294 regression: a
// zero-point comment_draft_card emitted with `amount: nil` was vanishing
// for this exact reason.
const optionalString = z.string().nullable().optional();
const optionalNumber = z.number().nullable().optional();
const optionalStringArray = z.array(z.string()).nullable().optional();
const optionalRecipient = z
	.union([z.string(), z.array(z.string())])
	.nullable()
	.optional();

/**
 * One focus variant of a recognition draft (BIZY-424). The giver and
 * recipient are shared at the card level; each variant foregrounds a
 * different facet of the recognition and carries its own message, points,
 * hashtags, an optional Gratitude Gauge `qualityScore` (e.g. "4/5"), and
 * its own actions (the send / edit payloads differ per variant). Switching
 * tabs is client-side only — every variant ships in the initial payload.
 */
const RecognitionDraftVariantSchema = z.object({
	focus: optionalString,
	message: z.string(),
	amount: optionalNumber,
	hashtags: optionalStringArray,
	qualityScore: optionalString,
	actions: ActionListSchema,
});
export type RecognitionDraftVariant = z.infer<typeof RecognitionDraftVariantSchema>;

/** Tolerant variant list — a malformed variant drops out rather than failing the whole card. */
const VariantListSchema = tolerantList(RecognitionDraftVariantSchema).optional();

/**
 * Draft card for writing a recognition. The full recognition shape is
 * visible: giver (the current user), recipient(s), points, message,
 * hashtags. Typical actions: `open_in_givebox` (primary) and optionally
 * `insert_into_draft` when a give box surface is already mounted.
 *
 * BIZY-424: when `variants` is present the card renders tone tabs and the
 * top-level message/amount/hashtags/actions mirror the first variant, so
 * a client that ignores `variants` still shows a valid single draft.
 */
export const RecognitionDraftCardSchema = z.object({
	type: z.literal('recognition_draft_card'),
	blockId: BlockIdSchema,
	payload: z.object({
		giver: optionalString,
		recipient: optionalRecipient,
		amount: optionalNumber,
		message: z.string(),
		hashtags: optionalStringArray,
		actions: ActionListSchema,
		variants: VariantListSchema,
	}),
});

/**
 * Draft card for writing a comment or add-on. No recipient tag, amount
 * and hashtags are both optional (a comment is zero-point text by
 * default; an add-on adds +N). Typical actions: `insert_into_draft`
 * (primary) + `send_now` (ghost secondary).
 */
export const CommentDraftCardSchema = z.object({
	type: z.literal('comment_draft_card'),
	blockId: BlockIdSchema,
	payload: z.object({
		message: z.string(),
		amount: optionalNumber,
		hashtags: optionalStringArray,
		parentBonusId: optionalString,
		actions: ActionListSchema,
	}),
});

/**
 * Read-only reference to a real recognition post. Shows the original
 * giver/recipient/amount/message/hashtags/date and a View post link.
 * Used when the user asks "show me X's last recognition".
 */
export const RecognitionViewCardSchema = z.object({
	type: z.literal('recognition_view_card'),
	blockId: BlockIdSchema,
	payload: z.object({
		giver: optionalString,
		recipient: optionalRecipient,
		amount: optionalNumber,
		message: z.string(),
		hashtags: optionalStringArray,
		date: optionalString,
		url: optionalString,
		actions: ActionListSchema,
	}),
});

/**
 * Pollinator mode card. A snapshot of an existing recognition
 * post or celebration the user hasn't engaged with yet, paired with
 * actions to add their appreciation: "Add on" (a points-bearing pile-on)
 * and/or "Comment" (a zero-point reply), plus "View post".
 *
 * Same display shape as {@link RecognitionViewCardSchema} (it's a real
 * post), with two differences:
 *   - `kind` distinguishes a peer post from a celebration (birthday,
 *     work anniversary, welcome / new-hire) so the renderer can label it.
 *   - actions are add-on / comment (carrying `parentBonusId`) rather than
 *     just "View post".
 *
 * Backend wire contract: the emitting tool MUST use these
 * field names. `amount` is the SNAPSHOT post's points; the SUGGESTED add-on
 * amount rides on the "Add on" `send_now` action (`amount`), not the
 * payload. `z.object` strips unknown keys, so emitting e.g. `recipients`
 * or `suggestedAmount` would silently drop the recipient handle / points
 * pill rather than fail — keep the names in sync.
 */
export const RecognitionAppreciationCardSchema = z.object({
	type: z.literal('recognition_appreciation_card'),
	blockId: BlockIdSchema,
	payload: z.object({
		kind: z.enum(['peer', 'celebration']).nullable().optional(),
		giver: optionalString,
		recipient: optionalRecipient,
		amount: optionalNumber,
		message: z.string(),
		hashtags: optionalStringArray,
		date: optionalString,
		actions: ActionListSchema,
	}),
});

// ─── User-admin cards (INS-1011) ─────────────────────────────────────────────
//
// Every user-admin write (invite / update / deactivate / reactivate),
// whether it targets one user or many, previews through ONE card:
// {@link UserAdminBatchConfirmCardSchema}. A single-user action is just a
// batch of one item, so there's a single schema + renderer instead of a
// card per action. The descriptive payload mirrors the shared
// `Users::DraftPayloads::UserAdminBatchConfirm` module (one source of
// truth, also returned as MCP `structuredContent`); the surface layers
// `actions` (Confirm / Cancel) on top. `version` is the payload schema
// version, carried for forward-compat. Optional fields are
// `.nullable().optional()` for the same `null`-from-Mongoid reason above.

/** A single before/after field diff on a batch item (update changes). */
const UserUpdateChangeSchema = z.object({
	field: z.string(),
	before: optionalString,
	after: optionalString,
});

/**
 * Tolerant change-list parser — drops a malformed diff row rather than
 * dropping the whole card (mirrors {@link ActionListSchema}).
 */
const ChangeListSchema = tolerantList(UserUpdateChangeSchema);

/** A single `{label, value}` field row on a batch item (invite fields). */
const UserAdminFieldRowSchema = z.object({
	label: z.string(),
	value: optionalString,
});

const FieldRowListSchema = tolerantList(UserAdminFieldRowSchema);

/**
 * One user within a batch card. Generic so a single renderer covers every
 * action: `title`/`subtitle` label the person, `rows` carry invite field
 * values, `changes` carry update before/after diffs. Tolerant sub-parsers
 * drop malformed rows rather than dropping the whole item.
 */
const UserAdminBatchItemSchema = z.object({
	title: z.string(),
	subtitle: optionalString,
	rows: FieldRowListSchema.optional(),
	changes: ChangeListSchema.optional(),
});

const BatchItemListSchema = tolerantList(UserAdminBatchItemSchema).default([]);

/** A skipped entry the batch won't process, shown muted with its reason. */
const UserAdminSkippedSchema = z.object({
	label: z.string(),
	reason: optionalString,
});

const SkippedListSchema = tolerantList(UserAdminSkippedSchema);

/**
 * Confirmation card for a BATCH user-admin action (invite / update /
 * deactivate / reactivate of more than one user). One card represents the
 * whole batch — Confirm replays the action with `confirmed=true` and the
 * full list, so it commits exactly the users shown here.
 */
export const UserAdminBatchConfirmCardSchema = z.object({
	type: z.literal('user_admin_batch_confirm_card'),
	blockId: BlockIdSchema,
	payload: z.object({
		version: z.string(),
		action: z.string(),
		title: optionalString,
		consequence: optionalString,
		items: BatchItemListSchema,
		skipped: SkippedListSchema.optional(),
		actions: ActionListSchema,
	}),
});

// ─── Block union + unknown fallback ──────────────────────────────────────────

/** Sentinel for blocks whose `type` the current client doesn't recognize. */
export const UnknownBizyUiBlockSchema = z.object({
	type: z.string(),
	blockId: BlockIdSchema,
	payload: z.unknown().optional(),
});

export type UnknownBizyUiBlock = z.infer<typeof UnknownBizyUiBlockSchema>;

/**
 * The primary known-block union. Don't use this schema directly to
 * parse a wire event — use {@link BizyUiBlockSchema} below, which falls
 * back to `UnknownBizyUiBlock` for unrecognized types instead of
 * rejecting the message outright.
 */
const KnownBizyUiBlockSchema = z.discriminatedUnion('type', [
	RecognitionDraftCardSchema,
	CommentDraftCardSchema,
	RecognitionViewCardSchema,
	RecognitionAppreciationCardSchema,
	UserAdminBatchConfirmCardSchema,
]);

/**
 * Parses a UI block from the wire. Known types land on the typed
 * variants above; unknown types land on {@link UnknownBizyUiBlockSchema}
 * so the renderer can show a graceful placeholder instead of dropping
 * the block silently. Invalid shapes (missing `type`, etc.) still fail.
 */
export const BizyUiBlockSchema = z.union([KnownBizyUiBlockSchema, UnknownBizyUiBlockSchema]);

export type BizyUiBlock = z.infer<typeof BizyUiBlockSchema>;
export type RecognitionDraftCard = z.infer<typeof RecognitionDraftCardSchema>;
export type CommentDraftCard = z.infer<typeof CommentDraftCardSchema>;
export type RecognitionViewCard = z.infer<typeof RecognitionViewCardSchema>;
export type RecognitionAppreciationCard = z.infer<typeof RecognitionAppreciationCardSchema>;
export type UserUpdateChange = z.infer<typeof UserUpdateChangeSchema>;
export type UserAdminBatchConfirmCard = z.infer<typeof UserAdminBatchConfirmCardSchema>;
export type UserAdminBatchItem = z.infer<typeof UserAdminBatchItemSchema>;

/** Every `type` string that resolves to a typed (non-unknown) block. */
const KNOWN_BIZY_UI_BLOCK_TYPES = [
	'recognition_draft_card',
	'comment_draft_card',
	'recognition_view_card',
	'recognition_appreciation_card',
	'user_admin_batch_confirm_card',
] as const;

type KnownBizyUiBlock = RecognitionDraftCard | CommentDraftCard | RecognitionViewCard | RecognitionAppreciationCard | UserAdminBatchConfirmCard;

/**
 * Narrow a parsed block to the known-type variants — useful when a
 * caller wants to branch on `type` with exhaustive switch safety.
 */
export function isKnownBizyUiBlock(block: BizyUiBlock): block is KnownBizyUiBlock {
	return (KNOWN_BIZY_UI_BLOCK_TYPES as readonly string[]).includes(block.type);
}

/**
 * Array parser — drops items that fail validation entirely so one bad
 * block doesn't take down a whole message's worth of cards.
 */
export const BizyUiBlockListSchema = z
	.array(z.unknown())
	.transform((raw) => raw.map((item) => BizyUiBlockSchema.safeParse(item)).flatMap((result) => (result.success ? [result.data] : [])))
	.default([]);
